/**
 * 城市层上的居民小角色（WORLD-RESIDENTS-ALIVE-117，6796）：页面把原来标记里的静态头像（MapPortrait）交进来当 portrait，这里给它套上「此刻在演什么」。
 * 头像怎么取（官方形象链、游客媒体、退路图）仍由页面那颗 MapPortrait 决定，这里一点不改；点开、读屏、定位、合并都照旧归页面（6c2b、UI-2）。
 * 整块 aria-hidden：读屏由页面那颗按钮说。
 * - 表情沿用 PetMoodAvatar 那一套样式（pet-mood.css 的 .ps-mood 结构：睡觉冒 z Z z、走路一颠一颠加爪印、四处看看、呼吸），和主人自家地图上的同一个样子；
 * - 在家（睡着、醒着、说不清睡没睡）不挂小标：城市层只给例外挂小标，平常状态珊瑚圈和屋里的 TA 已经说清；睡着由 z 和暗一点的脸来说，
 *   说不清睡没睡只画 TA 的静态头像、不演动作（UI2-CITY-ACTORS-117，I 10-16 产品口径；6796 10-16 授权改本文件）；
 * - 到访挂一枚实底小标（指南针）；
 * - 给了 pet_id 就按它错开动画相位（--actor-phase，actors.css 用），一屏的小角色不同时冒 z、同时迈步；
 * - 说不清在哪：头像暗着，不演；
 * - 减少动态时全部静止（pet-mood.css 与 actors.css 都在 prefers-reduced-motion 下停掉动画）；页面面板盖住地图时传 paused 暂停。
 * 不镜像：任何朝向都用原图，左右不对称的花纹不会错。
 */
import type { CSSProperties, ReactNode } from "react";
import type { PublicMapResident } from "@/shared/contracts";
import { Icon } from "@/shared/ui";
import { actorMood, actorScene, actorSign, type ScreenPoint } from "./actorModel";
import "../pet-mood.css";
import "./actors.css";

export interface ResidentActorProps {
  /** pet_id 可选：给了就按它错开动画相位 */
  resident: Pick<PublicMapResident, "presence" | "asleep"> & { pet_id?: string };
  /** 页面原来标记里的那颗头像（MapPortrait），原样放进来 */
  portrait: ReactNode;
  /** 头像直径（像素），和页面给 MapPortrait 的一样 */
  size: number;
  /** 面板盖住地图、页面藏起来时暂停动效 */
  paused?: boolean;
}

/**
 * 按 pet_id 给一个稳定的相位（0～1，三位小数）：同一位每次一样，不同的几位错开。FNV-1a 再过一遍 murmur3 的收尾混合——
 * 只用 FNV-1a 时，末位不同的 id（PJ-SZ0、PJ-SZ1…）相位只差千分之几，等于没错开。
 */
export function actorPhase(petId: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < petId.length; i++) {
    h ^= petId.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return Math.floor(((h >>> 0) / 0x100000000) * 1000) / 1000;
}

function MoodFx({ mood }: { mood: ReturnType<typeof actorMood> }) {
  if (mood === "sleeping")
    return (
      <span className="ps-mood__zzz">
        <i>z</i>
        <i>z</i>
        <i>Z</i>
      </span>
    );
  if (mood === "walking")
    return (
      <span className="ps-mood__steps">
        <i />
        <i />
      </span>
    );
  if (mood === "exploring")
    return (
      <span className="ps-mood__spark">
        <i>✦</i>
        <i>✦</i>
        <i>✧</i>
      </span>
    );
  return null;
}

export function ResidentActor({ resident, portrait, size, paused = false }: ResidentActorProps) {
  const scene = actorScene(resident);
  const mood = actorMood(scene);
  const sign = actorSign(scene);
  const glyph = Math.max(11, Math.round(size * 0.32));
  const phase = resident.pet_id ? actorPhase(resident.pet_id) : 0;
  return (
    <span className="ps-actor" data-actor-scene={scene} data-paused={paused ? "" : undefined} style={{ "--actor-size": `${size}px`, "--actor-phase": phase } as CSSProperties} aria-hidden="true">
      <span className={`ps-mood${mood ? ` is-${mood}` : " is-still"}`} style={{ "--mood-size": `${size}px` } as CSSProperties} data-mood={mood ?? undefined}>
        <span className="ps-mood__body">{portrait}</span>
        <MoodFx mood={mood} />
      </span>
      {sign === "place" ? (
        <span className="ps-actor__place">
          <Icon name="compass" size={glyph} />
        </span>
      ) : null}
    </span>
  );
}

/**
 * 在路上这一程的示意虚线（两片区中心之间，只到片区，不画街道级路线）。两端是页面用同一投影算好的屏幕坐标（相对同一个定位容器）；
 * 页面把 TA 的标记放在 routePoint(from, to, routeProgress(move, now)) 上。静态线，不动；读屏不读（页面那颗按钮说「在路上」）。
 * 终点一端画一个小箭头，尖落在目的地、朝着来的反方向（UI2-CITY-ACTORS-117 设计评审中 3：原来只有一个小圆点，看不出往哪走）。
 */
export function ResidentRoute({ from, to }: { from: ScreenPoint; to: ScreenPoint }) {
  const left = Math.min(from.x, to.x) - 4;
  const top = Math.min(from.y, to.y) - 4;
  const width = Math.abs(to.x - from.x) + 8;
  const height = Math.abs(to.y - from.y) + 8;
  const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  const ux = (to.x - from.x) / length;
  const uy = (to.y - from.y) / length;
  const tip = { x: to.x - left, y: to.y - top };
  const arrow = [
    [tip.x, tip.y],
    [tip.x - ux * 7 - uy * 4, tip.y - uy * 7 + ux * 4],
    [tip.x - ux * 7 + uy * 4, tip.y - uy * 7 - ux * 4],
  ]
    .map((point) => point.map((v) => Math.round(v * 10) / 10).join(","))
    .join(" ");
  return (
    <svg className="ps-actor-route" style={{ left, top, width, height }} width={width} height={height} aria-hidden="true">
      <line x1={from.x - left} y1={from.y - top} x2={to.x - left} y2={to.y - top} />
      <polygon points={arrow} />
    </svg>
  );
}
